import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import type { StatusToken } from '@sleed/bizcommerce-api';
import { statusesApi, binaryRepoImagesApi } from '~/lib/api/client';
import { formToToken, type StatusFormValues } from '~/lib/schemas/status';

export const statusKeys = {
	all: ['statuses'] as const,
	list: () => [...statusKeys.all, 'list'] as const,
	detail: (id: number | string) => [...statusKeys.all, 'detail', id] as const
};

export function useStatusesList() {
	return useQuery<StatusToken[]>({
		queryKey: statusKeys.list(),
		queryFn: async () => (await statusesApi.getAll()).data.items ?? []
	});
}

/** Single status by id (full record incl. SEO/body/scheduling). `create` → null. */
export function useStatus(id: string) {
	const numericId = Number(id);
	const enabled = Number.isFinite(numericId) && numericId > 0;
	return useQuery<StatusToken | null>({
		queryKey: statusKeys.detail(id),
		queryFn: async () => (enabled ? (await statusesApi.getById(numericId)).data : null),
		enabled
	});
}

/**
 * Upload the status image into slot `key` ("1"). The status must already exist (id > 0); the generated
 * client dropped the multipart param, so we attach the File as form-data keyed by the slot subkey
 * (mirrors the banner upload + the legacy BinaryRepoImages/UploadStatus contract).
 */
export async function uploadStatusImage(statusId: number, key: '1', file: File): Promise<void> {
	const fd = new FormData();
	fd.append(key, file);
	await binaryRepoImagesApi.uploadStatus(statusId, key, { data: fd });
}

export function useStatusMutations() {
	const qc = useQueryClient();
	const invalidate = () => qc.invalidateQueries({ queryKey: statusKeys.all });
	const create = useMutation({
		mutationFn: async (form: StatusFormValues) => (await statusesApi.insert(formToToken(form))).data,
		onSuccess: invalidate
	});
	const update = useMutation({
		mutationFn: async (form: StatusFormValues) => (await statusesApi.update(formToToken(form))).data,
		onSuccess: invalidate
	});
	const remove = useMutation({ mutationFn: async (id: number) => (await statusesApi.remove(id)).data, onSuccess: invalidate });
	return { create, update, remove };
}
